import React, { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, Crown, Medal } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Renders the top-N times for a course + the caller's personal best
// (with global position) even when they didn't crack the top N.
// `refreshKey` lets a parent force-reload after a race finish.
export default function CourseLeaderboard({ courseId, refreshKey = 0, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${BACKEND_URL}/api/courses/${courseId}/leaderboard?limit=${compact ? 5 : 10}`,
          { withCredentials: true },
        );
        if (!cancelled) setData(r.data);
      } catch (_e) {
        if (!cancelled) setData({ leaderboard: [], my_best: null });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId, refreshKey, compact]);

  if (loading && !data) {
    return (
      <div className="text-white/40 text-xs text-center py-4" data-testid="course-leaderboard-loading">
        Loading…
      </div>
    );
  }
  const rows = data?.leaderboard || [];
  const my = data?.my_best;
  const myPos = data?.my_position;

  // Render the caller's position differently when they're outside the top N.
  const myInTop = !!(my && rows.some((r) => r.time_seconds === my.time_seconds && r.name && myPos && myPos <= rows.length));

  return (
    <div data-testid={`course-leaderboard-${courseId}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-[#FFCC00]" />
          <div className="font-heading text-base">
            {compact ? "Top times" : "Course Leaderboard"}
          </div>
        </div>
        {my && (
          <div className="text-[10px] tracking-widest text-white/40 uppercase font-mono">
            #{myPos} · {fmt(my.time_seconds)}
          </div>
        )}
      </div>
      {rows.length === 0 ? (
        <div
          className="border border-white/10 p-5 text-center text-white/40 text-xs"
          data-testid={`course-leaderboard-${courseId}-empty`}
        >
          No finishes yet. Be the first to clear this heist.
        </div>
      ) : (
        <div className="space-y-1">
          {rows.map((row, idx) => {
            const rank = idx + 1;
            const isMine = my && my.time_seconds === row.time_seconds && row.name === my.name;
            return (
              <div
                key={`${row.user_id}-${row.time_seconds}`}
                data-testid={`course-leaderboard-${courseId}-row-${rank}`}
                className={`flex items-center gap-3 px-3 py-2 border ${
                  isMine
                    ? "border-[#FFCC00] bg-[#FFCC00]/5"
                    : "border-white/5"
                }`}
              >
                <span className={`w-7 text-center font-heading text-base ${rankColor(rank)}`}>
                  {rank === 1 ? <Crown className="w-4 h-4 inline text-[#FFCC00]" /> :
                   rank === 2 ? <Medal className="w-4 h-4 inline text-[#C0C0C0]" /> :
                   rank === 3 ? <Medal className="w-4 h-4 inline text-[#CD7F32]" /> :
                   `#${rank}`}
                </span>
                <span className="flex-1 truncate text-sm">
                  {row.name}{isMine ? " (you)" : ""}
                </span>
                <span className="font-mono text-sm text-[#FFCC00]">
                  {fmt(row.time_seconds)}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {my && !myInTop && myPos > rows.length && (
        <div
          className="mt-2 flex items-center gap-3 px-3 py-2 border border-[#FFCC00] bg-[#FFCC00]/5"
          data-testid={`course-leaderboard-${courseId}-mine`}
        >
          <span className="w-7 text-center font-heading text-base text-white/70">
            #{myPos}
          </span>
          <span className="flex-1 truncate text-sm">You</span>
          <span className="font-mono text-sm text-[#FFCC00]">{fmt(my.time_seconds)}</span>
        </div>
      )}
    </div>
  );
}

function rankColor(rank) {
  if (rank === 1) return "text-[#FFCC00]";
  if (rank === 2) return "text-[#C0C0C0]";
  if (rank === 3) return "text-[#CD7F32]";
  return "text-white/50";
}

function fmt(seconds) {
  const s = Number(seconds);
  if (!isFinite(s)) return "—";
  if (s < 60) return `${s.toFixed(2)}s`;
  const m = Math.floor(s / 60);
  const r = (s - m * 60).toFixed(2);
  return `${m}:${String(r).padStart(5, "0")}`;
}
